// ProcessContext — global batch process monitor.
// Tracks background batch operations with per-item status, fix/retry callbacks,
// beforeunload protection, and localStorage persistence for crash recovery.

import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  ProcessContext,
  useProcesses,
  type BatchItem,
  type BatchItemStatus,
  type Process,
  type ItemCallbacks,
} from "@ds/actions/useProcesses";

export type {
  BatchItem,
  BatchItemStatus,
  Process,
  ProcessStatus,
  ItemCallbacks,
} from "@ds/actions/useProcesses";


// ── localStorage persistence ────────────────────────────────────────────

const STORAGE_PREFIX = "process-monitor:";
const STALE_TTL = 60 * 60 * 1000; // 1 hour

interface PersistedProcess {
  id: string;
  type: string;
  label: string;
  description?: string;
  items: BatchItem[];
  phases?: string[];
  createdAt: string;
  metadata?: Record<string, unknown>;
}

function persistProcess(process: Process) {
  try {
    const data: PersistedProcess = {
      id: process.id, type: process.type, label: process.label,
      description: process.description, items: process.items,
      phases: process.phases, createdAt: process.createdAt.toISOString(),
      metadata: process.metadata,
    };
    localStorage.setItem(`${STORAGE_PREFIX}${process.id}`, JSON.stringify(data));
  } catch { /* quota exceeded — non-critical */ }
}

function removePersistedProcess(id: string) {
  try { localStorage.removeItem(`${STORAGE_PREFIX}${id}`); } catch { /* ignore */ }
}

function loadStaleProcesses(): Process[] {
  const stale: Process[] = [];
  const now = Date.now();
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const data = JSON.parse(raw) as PersistedProcess;
      const createdAt = new Date(data.createdAt);
      if (now - createdAt.getTime() > STALE_TTL) {
        localStorage.removeItem(key);
        continue;
      }
      // Only show as stale if it had unfinished items
      const hasUnfinished = data.items.some(i => i.status === "pending" || i.status === "processing");
      if (!hasUnfinished) {
        localStorage.removeItem(key);
        continue;
      }
      // An unfinished item waiting on a server job survives a reload — a watcher re-resolves
      // it from the DB. Only unfinished items with no job are lost client-side work.
      const hasJobWaiting = data.items.some(i =>
        (i.status === "pending" || i.status === "processing") && i.jobId,
      );
      const items = data.items.map(item =>
        (item.status === "processing" || item.status === "pending") && !item.jobId
          ? { ...item, status: "error" as const, message: "Przerwano — odśwież stronę" }
          : item,
      );
      stale.push({
        id: data.id, type: data.type, label: data.label, description: data.description,
        status: hasJobWaiting ? "running" : "stale", items, phases: data.phases,
        createdAt, completedAt: hasJobWaiting ? undefined : new Date(), metadata: data.metadata,
      });
    }
  } catch { /* corrupt storage — ignore */ }
  return stale;
}

/** Completion body shared by `completeProcess` and `settleProcess`. */
function finishProcess(p: Process): Process {
  const hasProblems = p.items.some(i => i.status === "error" || i.status === "warning");
  const completed: Process = {
    ...p,
    status: hasProblems ? "completed_with_errors" : "completed",
    completedAt: new Date(),
  };
  if (!hasProblems) removePersistedProcess(p.id);
  else persistProcess(completed);
  return completed;
}

// ── Provider ────────────────────────────────────────────────────────────

export const ProcessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [processes, setProcesses] = useState<Process[]>(() => loadStaleProcesses());
  const [fixModal, setFixModal] = useState<React.ReactNode>(null);
  const callbacksRef = useRef<Map<string, Map<string, ItemCallbacks>>>(new Map());
  // Callbacks live in the ref above (not state) so registering them doesn't touch
  // `processes` — but consumers (e.g. a "Ponów" button) still need to see a newly
  // registered callback immediately, so this bumps a counter to force the provider
  // (and every context consumer) to re-render.
  const [, bumpCallbacksVersion] = useState(0);

  // ── beforeunload guard ──────────────────────────────────
  // Only an item still working client-side (no jobId) should hold the tab open — an item
  // waiting on a server job survives a reload and is resolved by a watcher afterwards.
  useEffect(() => {
    const hasRunning = processes.some(p =>
      p.status === "running" &&
      p.items.some(i => (i.status === "pending" || i.status === "processing") && !i.jobId),
    );
    if (!hasRunning) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [processes]);

  // ── Auto-cleanup completed processes after 30s ──────────
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setProcesses(prev => prev.filter(p => {
        if (p.status === "running") return true;
        if (p.status === "completed_with_errors") return true;
        if (p.status === "stale") return true;
        if (!p.completedAt) return true;
        return now - p.completedAt.getTime() < 30000;
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // ── Lifecycle ───────────────────────────────────────────

  const startProcess = useCallback((
    type: string, label: string, items: BatchItem[],
    metadata?: Record<string, unknown>, description?: string, phases?: string[],
  ): string => {
    const id = `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const process: Process = {
      id, type, label, description, status: "running",
      items, phases, createdAt: new Date(), metadata,
    };
    setProcesses(prev => [...prev, process]);
    persistProcess(process);
    return id;
  }, []);

  const updateItem = useCallback((
    processId: string, itemId: string, status: BatchItemStatus, message?: string, phase?: string,
  ) => {
    setProcesses(prev => prev.map(p => {
      if (p.id !== processId) return p;
      const updated = {
        ...p,
        items: p.items.map(item =>
          item.id === itemId ? { ...item, status, message, ...(phase ? { phase } : {}) } : item,
        ),
      };
      persistProcess(updated);
      return updated;
    }));
  }, []);

  const completeProcess = useCallback((processId: string) => {
    setProcesses(prev => prev.map(p => (p.id === processId ? finishProcess(p) : p)));
  }, []);

  // Server-side counterpart of `completeProcess` — a watcher calls this after every poll/
  // realtime tick, so it must bail out to the SAME array (no map()) when nothing changed,
  // or it would re-render every consumer on every tick forever.
  const settleProcess = useCallback((processId: string) => {
    setProcesses(prev => {
      const idx = prev.findIndex(p => p.id === processId);
      const p = idx === -1 ? undefined : prev[idx];
      const changed = !!p && p.status === "running" &&
        !p.items.some(i => i.status === "pending" || i.status === "processing");
      if (!changed) return prev;
      const next = prev.slice();
      next[idx] = finishProcess(p);
      return next;
    });
  }, []);

  const awaitJob = useCallback((processId: string, itemId: string, jobId: string, message?: string) => {
    setProcesses(prev => prev.map(p => {
      if (p.id !== processId) return p;
      const updated = {
        ...p,
        items: p.items.map(item =>
          item.id === itemId
            ? { ...item, status: "processing" as const, jobId, message, waitingSince: Date.now() }
            : item,
        ),
      };
      persistProcess(updated);
      return updated;
    }));
  }, []);

  const reopenProcess = useCallback((processId: string) => {
    setProcesses(prev => prev.map(p => {
      if (p.id !== processId) return p;
      const updated: Process = { ...p, status: "running", completedAt: undefined };
      persistProcess(updated);
      return updated;
    }));
  }, []);

  const removeProcess = useCallback((id: string) => {
    setProcesses(prev => prev.filter(p => p.id !== id));
    callbacksRef.current.delete(id);
    removePersistedProcess(id);
  }, []);

  const clearCompleted = useCallback(() => {
    setProcesses(prev => {
      const remaining = prev.filter(p => p.status === "running");
      prev.filter(p => p.status !== "running").forEach(p => {
        callbacksRef.current.delete(p.id);
        removePersistedProcess(p.id);
      });
      return remaining;
    });
  }, []);

  const getProcess = useCallback((id: string): Process | undefined => {
    return processes.find(p => p.id === id);
  }, [processes]);

  const isRunning = useCallback((type: string, metadata?: Record<string, unknown>): boolean => {
    return processes.some(p => {
      if (p.status !== "running" || p.type !== type) return false;
      if (metadata) return Object.entries(metadata).every(([k, v]) => p.metadata?.[k] === v);
      return true;
    });
  }, [processes]);

  // ── Callback registry ──────────────────────────────────

  const registerCallbacks = useCallback((processId: string, callbacks: Map<string, ItemCallbacks>) => {
    callbacksRef.current.set(processId, callbacks);
    bumpCallbacksVersion(v => v + 1);
  }, []);

  const getCallbacks = useCallback((processId: string, itemId: string): ItemCallbacks | undefined => {
    return callbacksRef.current.get(processId)?.get(itemId);
  }, []);

  return (
    <ProcessContext.Provider value={{
      processes,
      startProcess, updateItem, completeProcess, settleProcess, awaitJob, reopenProcess,
      removeProcess, clearCompleted,
      getProcess, isRunning, registerCallbacks, getCallbacks,
      fixModal, setFixModal,
    }}>
      {children}
    </ProcessContext.Provider>
  );
};

/**
 * Renders the active fix modal. Must be mounted somewhere inside the Refine
 * provider tree so modal contents (e.g., PrepareEmailModal with useOne) have
 * access to QueryClient.
 */
export const ProcessFixModalSlot: React.FC = () => {
  const { fixModal } = useProcesses();
  return <>{fixModal}</>;
};
